<template>
  <DefaultLayout>
    <div class="container my-8 px-6 mx-auto">
      <FormContainer
        title="Guardian Angel Home Insurance Quote"
        :is-loading="isSubmitting"
        loading-message="Submitting your quote request..."
        :error-message="formError"
        :success-message="successMessage"
        :submit-label="isSuccess ? 'Submit Another Quote' : 'Submit'"
        @submit="submitForm"
        @retry="retrySubmission"
        @contact-support="contactSupport"
        @clear-error="clearErrorAndReset"
      >
        <!-- Product icon -->
        <div class="flex justify-center mb-6">
          <img :src="homeInsuranceIcon" class="w-24" alt="Home Insurance" />
        </div>
        
        <!-- Form content -->
        <div v-if="!isSuccess">
          <!-- Personal Information Section -->
          <div class="mb-6">
            <h3 class="text-lg font-medium text-gray-700 mb-4">Personal Information</h3>
            
            <FormField
              id="firstName"
              label="First Name"
              v-model="formData.firstName"
              :error="!!errors.firstName"
              :error-message="errors.firstName"
              required
              @blur="validateField('firstName')"
            />
            
            <FormField
              id="lastName"
              label="Last Name"
              v-model="formData.lastName"
              :error="!!errors.lastName"
              :error-message="errors.lastName"
              required
              @blur="validateField('lastName')"
            />
            
            <FormField
              id="email"
              type="email"
              label="Email Address"
              v-model="formData.email"
              :error="!!errors.email"
              :error-message="errors.email"
              required
              @blur="validateField('email')"
            />
            
            <FormField
              id="phone"
              label="Phone Number"
              v-model="formData.phone"
              mask="(999) 999-9999"
              :error="!!errors.phone"
              :error-message="errors.phone"
              required
              @blur="validateField('phone')"
            />
          </div>
          
          <!-- Additional Information Section -->
          <div class="mb-6">
            <h3 class="text-lg font-medium text-gray-700 mb-4">Additional Information</h3>
            
            <FormField
              id="message"
              type="textarea"
              label="Your Message"
              v-model="formData.message"
              rows="4"
              placeholder="Please provide any details that would help us better understand your insurance needs (property size, year built, renovations, etc.)"
            />
          </div>

          <!-- File Upload Section -->
          <div class="mb-6">
            <h3 class="text-lg font-medium text-gray-700 mb-4">Optional Documents</h3>
            
            <div v-for="(field, type) in uploadFields" :key="type" class="mb-4 bg-gray-50 p-4 rounded-lg">
              <label :for="type" class="block text-sm font-medium text-gray-700 mb-2">
                {{ field.label }}
                <span class="help-icon">
                  <span class="question-mark">?</span>
                  <span class="help-content">
                    {{ field.tooltip }}
                  </span>
                </span>
              </label>
              
              <!-- File upload zone -->
              <div
                class="upload-zone"
                @click="triggerFileInput(type)"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop($event, type)"
              >
                <input
                  :id="type"
                  type="file"
                  class="hidden"
                  :accept="allowedExtensions.join(',')"
                  @change="(e) => handleFileChange(type, e)"
                />
                
                <!-- Empty state -->
                <div v-if="!files[type]" class="empty-state">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div class="text-sm text-gray-600">Drag & drop or click to upload</div>
                  <p class="text-xs text-gray-400 mt-1">Accepted files: PDF, DOC, DOCX, JPG, PNG</p>
                </div>
                
                <!-- File selected state -->
                <div v-else class="file-selected">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span class="text-sm font-medium text-gray-900">{{ files[type].name }}</span>
                  </div>
                  <button
                    @click.stop="removeFile(type)"
                    class="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <p class="text-sm text-gray-500">
            Fields marked with <span class="text-red-500">*</span> are required.
          </p>
        </div>
        
        <!-- Success view -->
        <div v-else class="text-center py-8">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckIcon class="w-8 h-8 text-green-500" />
          </div>
          <h3 class="text-xl font-medium text-gray-800 mb-2">Quote Request Submitted!</h3>
          <p class="text-gray-600 mb-6">
            Thank you for your interest. One of our Guardian Angel specialists will contact you shortly.
          </p>
        </div>
      </FormContainer>
    </div>
  </DefaultLayout>
</template>

<script>
import { ref, computed } from 'vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import FormContainer from '@/components/common/FormContainer.vue';
import FormField from '@/components/common/FormField.vue';
import CheckIcon from '@/components/icons/CheckIcon.vue';
import { useNotifications } from '@/composables/ui/useNotifications';
import { validateForm, validationSchemas, isNotEmpty } from '@/utils/formValidation';
import axios from 'axios';

// Import home insurance icon
import homeInsuranceIcon from '@/assets/images/products/icons/home/icon-home-main.svg';

export default {
  name: 'GuardianAngelQuoteFormStandardized',
  
  components: {
    DefaultLayout,
    FormContainer,
    FormField,
    CheckIcon
  },
  
  props: {
    isEmbedded: {
      type: Boolean,
      default: false
    }
  },
  
  setup(props) {
    // Form data
    const formData = ref({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: ''
    });
    
    // File upload fields
    const files = ref({
      fourPoint: null,
      windMit: null,
      decPage: null
    });
    
    // Form state
    const isSubmitting = ref(false);
    const isSuccess = ref(false);
    const successMessage = ref('');
    const formError = ref('');
    const errors = ref({});
    const { showNotification } = useNotifications();
    
    // File upload configuration
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
    
    // Upload fields definition
    const uploadFields = {
      fourPoint: {
        label: 'Four Point Inspection',
        tooltip: 'A report that examines the four critical systems: HVAC, electrical, plumbing, and roofing.'
      },
      windMit: {
        label: 'Wind Mitigation Report',
        tooltip: 'A wind mitigation inspection identifies features that may reduce damage during a hurricane or strong windstorm.'
      },
      decPage: {
        label: 'Insurance Declaration Page',
        tooltip: 'The first page of your current policy that summarizes your coverage.'
      }
    };
    
    // Validate a single field
    const validateField = (field) => {
      const schema = validationSchemas.guardianAngel;
      
      if (schema[field]) {
        const { validate, message } = schema[field];
        const isValid = validate(formData.value[field]);
        
        if (!isValid) {
          errors.value[field] = message;
        } else {
          delete errors.value[field];
        }
        
        return isValid;
      }
      
      return true;
    };
    
    // Validate all fields
    const validateAll = () => {
      const { errors: validationErrors, isValid } = validateForm(formData.value, validationSchemas.guardianAngel);
      errors.value = validationErrors;
      return isValid;
    };
    
    // File handling methods
    const triggerFileInput = (fieldName) => {
      document.getElementById(fieldName).click();
    };
    
    const onDragOver = (event) => {
      event.target.classList.add('drag-over');
    };
    
    const onDragLeave = (event) => {
      event.target.classList.remove('drag-over');
    };
    
    const onDrop = (event, fieldName) => {
      event.target.classList.remove('drag-over');
      
      if (event.dataTransfer.files.length) {
        handleFileChange(fieldName, { target: { files: event.dataTransfer.files } });
      }
    };
    
    const handleFileChange = (fieldName, event) => {
      const file = event.target.files[0];
      
      if (!file) return;
      
      // Validate file size
      if (file.size > maxFileSize) {
        showNotification({ title: 'File Too Large', message: `File for ${uploadFields[fieldName].label} exceeds the 50MB limit.`, type: 'error' });
        return;
      }
      
      // Validate file type
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        showNotification({ title: 'Invalid File Type', message: `File type for ${uploadFields[fieldName].label} is not allowed.`, type: 'error' });
        return;
      }
      
      // Set file
      files.value[fieldName] = file;
      showNotification({ title: 'File Attached', message: `${file.name} attached successfully.`, type: 'success' });
    };
    
    const removeFile = (fieldName) => {
      files.value[fieldName] = null;
    };
    
    // Submit the form
    const submitForm = async () => {
      isSubmitting.value = true;
      formError.value = '';
      
      try {
        // Validate all fields
        if (!validateAll()) {
          isSubmitting.value = false;
          formError.value = 'Please correct the errors and try again.';
          return;
        }
        
        // Prepare form data with files
        const formDataToSend = new FormData();
        
        // Add form fields
        Object.keys(formData.value).forEach(key => {
          formDataToSend.append(key, formData.value[key]);
        });
        
        // Add files
        Object.keys(files.value).forEach(key => {
          if (files.value[key]) {
            formDataToSend.append(key, files.value[key]);
          }
        });
        
        // Submit to API
        const response = await axios.post('/api/submit-guardian-angel-quote', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json'
          }
        });
        
        // Handle success
        if (response.data.success) {
          isSuccess.value = true;
          successMessage.value = response.data.message || 'Guardian Angel quote request submitted successfully!';
          
          // Reset form
          resetForm();
          
          // Notify parent window if embedded
          if (props.isEmbedded) {
            window.parent.postMessage({
              type: 'formSubmitted',
              success: true,
              message: successMessage.value
            }, '*');
          }
        } else {
          // Handle API error
          formError.value = response.data.message || 'An error occurred submitting your quote request.';
          
          if (props.isEmbedded) {
            window.parent.postMessage({
              type: 'formError',
              message: formError.value
            }, '*');
          }
        }
      } catch (error) {
        // Handle validation errors
        if (error.response && error.response.status === 422) {
          const validationErrors = error.response.data.errors || {};
          
          errors.value = {
            ...errors.value,
            ...validationErrors
          };
          
          formError.value = error.response.data.message || 'Please correct the errors and try again.';
        } else {
          // Handle other errors
          formError.value = 'An unexpected error occurred. Please try again later.';
          showNotification({ title: 'Submission Error', message: 'An unexpected error occurred.', type: 'error' });
        }
        
        if (props.isEmbedded) {
          window.parent.postMessage({
            type: 'formError',
            message: formError.value
          }, '*');
        }
      } finally {
        isSubmitting.value = false;
      }
    };
    
    const resetForm = () => {
      formData.value = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      };
      
      files.value = {
        fourPoint: null,
        windMit: null,
        decPage: null
      };
      
      errors.value = {};
      
      if (props.isEmbedded) {
        window.parent.postMessage({
          type: 'formReset'
        }, '*');
      }
    };
    
    // Error handling methods
    const retrySubmission = () => {
      formError.value = '';
      errors.value = {};
      submitForm();
    };
    
    const contactSupport = () => {
      // Open support contact options
      const phoneNumber = '(954) 308-1800';
      const emailAddress = 'support@insuranceexpress.com';
      
      if (confirm(`Would you like to call us at ${phoneNumber}? Click Cancel to copy our email address instead.`)) {
        window.open(`tel:${phoneNumber}`);
      } else {
        // Copy email to clipboard
        navigator.clipboard.writeText(emailAddress).then(() => {
          showNotification({ 
            title: 'Email Copied', 
            message: `${emailAddress} copied to clipboard`, 
            type: 'success' 
          });
        });
      }
    };
    
    const clearErrorAndReset = () => {
      formError.value = '';
      errors.value = {};
      isSuccess.value = false;
      isSubmitting.value = false;
      resetForm();
    };

    return {
      formData,
      files,
      isSubmitting,
      isSuccess,
      successMessage,
      formError,
      errors,
      uploadFields,
      allowedExtensions,
      validateField,
      validateAll,
      triggerFileInput,
      onDragOver,
      onDragLeave,
      onDrop,
      handleFileChange,
      removeFile,
      submitForm,
      resetForm,
      retrySubmission,
      contactSupport,
      clearErrorAndReset,
      homeInsuranceIcon
    };
  }
};
</script>

<style scoped>
.help-icon {
  @apply relative inline-block ml-1 text-gray-500;
}

.question-mark {
  @apply inline-flex items-center justify-center w-4 h-4 rounded-full text-xs border border-gray-400;
  cursor: help;
}

.help-content {
  @apply absolute z-10 hidden p-2 w-48 text-xs bg-white border rounded shadow-lg -right-1;
  top: 100%;
}

.help-icon:hover .help-content {
  @apply block;
}

.upload-zone {
  @apply relative flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer transition-colors duration-300;
}

.upload-zone:hover {
  @apply border-blue-500 bg-blue-50;
}

.empty-state, .file-selected {
  @apply flex flex-col items-center justify-center w-full h-full p-4 text-center;
}

.file-selected {
  @apply justify-between;
}
</style> 